import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { Bell, Loader2, CheckCheck, PackageCheck, MessageSquare, ClipboardList, FileCheck, UserCheck, XCircle, Send } from "lucide-react";
import { useNavigate } from "react-router-dom";

const timeAgo = (iso, lang = "en") => {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return lang === "pt" ? "agora" : "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
};

const KIND_META = {
  mr_item_received:            { icon: PackageCheck, color: "bg-emerald-100 text-emerald-700", route: "/material-requests" },
  mr_comment:                  { icon: MessageSquare, color: "bg-slate-100 text-slate-600", route: "/material-requests" },
  project_submitted:           { icon: FileCheck,   color: "bg-purple-100 text-purple-700",   route: "/projects" },
  project_accepted:            { icon: UserCheck,   color: "bg-blue-100 text-blue-700",       route: "/projects" },
  project_pending_internal:    { icon: FileCheck,   color: "bg-amber-100 text-amber-800",     route: "/projects" },
  project_ready_for_client:    { icon: Send,        color: "bg-indigo-100 text-indigo-700",   route: "/projects" },
  project_rejected_internal:   { icon: XCircle,     color: "bg-rose-100 text-rose-700",       route: "/projects" },
  project_client_approved:     { icon: UserCheck,   color: "bg-emerald-100 text-emerald-700", route: "/projects" },
  project_client_rejected:     { icon: XCircle,     color: "bg-rose-100 text-rose-700",       route: "/projects" },
};

const iconFor = (kind) => (KIND_META[kind]?.icon) || ClipboardList;
const colorFor = (kind, unread) =>
  unread ? (KIND_META[kind]?.color || "bg-blue-100 text-blue-700") : "bg-slate-100 text-slate-500";
const routeFor = (n) => {
  if (n.data?.ticket_id) return "/tickets";
  if (n.data?.material_request_id) return "/material-requests";
  if (n.data?.project_id) return "/projects";
  return KIND_META[n.kind]?.route || null;
};

export default function NotificationBell() {
  const { t, lang } = useLang();
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(0);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const ref = useRef(null);

  const fetchCount = async () => {
    try { const r = await api.get("/notifications/count"); setCount(r.unread || 0); }
    catch { /* silent */ }
  };
  const fetchList = async () => {
    setLoading(true);
    try { setRows(await api.get("/notifications?limit=30")); }
    catch { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => {
    fetchCount();
    const it = setInterval(fetchCount, 30000); // poll every 30s
    return () => clearInterval(it);
  }, []);

  useEffect(() => {
    if (open) fetchList();
  }, [open]);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const markRead = async (n) => {
    if (!n.read) {
      try { await api.post(`/notifications/${n.id}/read`); }
      catch { /* silent */ }
      setRows((prev) => prev.map((x) => x.id === n.id ? { ...x, read: true } : x));
      setCount((c) => Math.max(0, c - 1));
    }
    // Navigate — projects, tickets, material requests
    const route = routeFor(n);
    if (route) navigate(route);
    setOpen(false);
  };

  const markAllRead = async () => {
    try { await api.post("/notifications/read-all"); }
    catch { /* silent */ }
    setRows((prev) => prev.map((x) => ({ ...x, read: true })));
    setCount(0);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="notif-bell"
        onClick={() => setOpen((v) => !v)}
        className="relative w-9 h-9 rounded-full flex items-center justify-center text-slate-500 hover:bg-slate-100 hover:text-slate-800 transition-colors"
        title={t("notifications") || "Notifications"}
      >
        <Bell className="w-5 h-5" />
        {count > 0 && (
          <span
            data-testid="notif-count"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center border-2 border-white"
          >
            {count > 99 ? "99+" : count}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notif-panel"
          className="absolute right-0 mt-2 w-96 max-w-[calc(100vw-2rem)] bg-white border border-slate-200 rounded-xl shadow-2xl overflow-hidden z-50"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50">
            <div className="text-sm font-semibold text-slate-900">
              {t("notifications") || "Notifications"}
              {count > 0 && (
                <span className="ml-2 text-[10px] font-bold text-red-600 bg-red-50 border border-red-100 rounded-full px-1.5 py-0.5">
                  {count} {t("new") || "new"}
                </span>
              )}
            </div>
            <button
              data-testid="notif-mark-all"
              onClick={markAllRead}
              disabled={count === 0}
              className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-600 hover:text-slate-900 disabled:opacity-40"
            >
              <CheckCheck className="w-3.5 h-3.5" /> {t("markAllRead") || "Mark all read"}
            </button>
          </div>

          <div className="max-h-96 overflow-y-auto">
            {loading && (
              <div className="flex justify-center py-6">
                <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
              </div>
            )}
            {!loading && rows.length === 0 && (
              <div className="text-center text-xs text-slate-400 py-10">
                {t("noNotifications") || "You're all caught up."}
              </div>
            )}
            {!loading && rows.map((n) => {
              const Icon = iconFor(n.kind);
              return (
                <button
                  key={n.id}
                  data-testid={`notif-item-${n.id}`}
                  onClick={() => markRead(n)}
                  className={`w-full text-left px-4 py-3 border-b border-slate-100 last:border-b-0 hover:bg-slate-50 transition-colors flex items-start gap-3 ${
                    n.read ? "bg-white" : "bg-blue-50/40"
                  }`}
                >
                  <div className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${colorFor(n.kind, !n.read)}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <div className={`text-sm truncate ${n.read ? "text-slate-700" : "font-semibold text-slate-900"}`}>
                        {n.title}
                      </div>
                      <span className="text-[10px] text-slate-400 whitespace-nowrap">
                        {timeAgo(n.created_at, lang)}
                      </span>
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5 line-clamp-2">{n.message}</div>
                  </div>
                  {!n.read && <span className="shrink-0 w-2 h-2 rounded-full bg-blue-500 mt-2" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
